
import React from 'react';
import Button from '../Button';

export interface Column<T> {
    header: React.ReactNode;
    accessor?: keyof T;
    render?: (item: T) => React.ReactNode;
    className?: string;
    headerClassName?: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    hidden?: boolean;
}

export interface TableProps<T> {
    data: T[];
    columns: Column<T>[];
    keyExtractor: (item: T) => string | number;
    loading?: boolean;
    /** `full` replaces the table with a spinner (default). `overlay` keeps headers and shows a spinner over the table body. */
    loadingVariant?: 'full' | 'overlay';
    emptyMessage?: string;
    onRowClick?: (item: T) => void;
    /** When set, right-click invokes your handler. Call `event.preventDefault()` when showing a custom menu so the browser menu does not open. */
    onRowContextMenu?: (event: React.MouseEvent<HTMLTableRowElement>, item: T) => void;
    pagination?: {
        currentPage: number;
        totalPages: number;
        onPageChange: (page: number) => void;
    };
    title?: string;
    description?: string;
    headerActions?: React.ReactNode;
    filters?: React.ReactNode;
    draggable?: boolean;
}

export default function Table<T>({
    data,
    columns,
    keyExtractor,
    loading = false,
    loadingVariant = 'full',
    emptyMessage = "No data available",
    onRowClick,
    onRowContextMenu,
    pagination,
    title,
    description,
    headerActions,
    filters,
    draggable = true,
}: TableProps<T>) {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [startX, setStartX] = React.useState(0);
    const [scrollLeft, setScrollLeft] = React.useState(0);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!draggable || !scrollContainerRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2; // scroll speed
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const visibleColumns = React.useMemo(() => columns.filter(col => !col.hidden), [columns]);

    if (loading && loadingVariant === 'full') {
        return (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-black/30">
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    const showOverlayLoader = loading && loadingVariant === 'overlay';

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-black/30">
            {/* Header Section */}
            {(title || description || headerActions) && (
                <div className="border-b border-gray-200 p-6 dark:border-gray-700">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            {title && (
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                    {title}
                                </h1>
                            )}
                            {description && (
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                    {description}
                                </p>
                            )}
                        </div>
                        {headerActions && (
                            <div>
                                {headerActions}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Filters Section */}
            {filters && (
                <div className="border-b border-gray-200 p-6 dark:border-gray-700">
                    {filters}
                </div>
            )}

            <div
                ref={scrollContainerRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                className={`relative overflow-x-auto rounded-b-xl ${draggable ? (isDragging ? 'cursor-grabbing select-none' : 'cursor-grab') : ''}`}
                style={{
                    scrollbarWidth: 'thin',
                    msOverflowStyle: 'auto',
                }}
            >
                {showOverlayLoader && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-b-xl bg-white/75 backdrop-blur-[1px] dark:bg-black/50">
                        <div className="text-center">
                            <div className="mb-3 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
                        </div>
                    </div>
                )}
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100 dark:border-gray-600 dark:from-gray-800/50 dark:to-gray-800/30">
                            {visibleColumns.map((column, index) => (
                                <th
                                    key={index}
                                    className={`border-r border-gray-200 px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300 ${column.headerClassName || ''} ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}`}
                                    style={{ width: column.width }}
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={visibleColumns.length} className="border-r border-gray-200 px-6 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400 bg-white dark:bg-black/30">
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : (
                            data.map((item, index) => (
                                <tr
                                    key={keyExtractor(item)}
                                    onClick={() => onRowClick && onRowClick(item)}
                                    onContextMenu={(e) => {
                                        if (!onRowContextMenu) return;
                                        onRowContextMenu(e, item);
                                    }}
                                    className={`transition-colors ${index % 2 === 0
                                        ? 'bg-white dark:bg-black/30'
                                        : 'bg-gray-50 dark:bg-gray-800/20'
                                        } hover:bg-blue-50 dark:hover:bg-blue-900/10 border-b border-gray-200 dark:border-gray-700 last:border-b-0 ${onRowClick ? 'cursor-pointer' : ''}`}
                                >
                                    {visibleColumns.map((column, colIndex) => (
                                        <td
                                            key={colIndex}
                                            className={`border-r border-gray-200 px-6 py-4 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100 ${column.className || ''} ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}`}
                                            style={{ width: column.width }}
                                        >
                                            {column.render
                                                ? column.render(item)
                                                : column.accessor
                                                    ? (item[column.accessor] as React.ReactNode)
                                                    : null
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="border-t border-gray-200 p-6 dark:border-gray-700">
                    <div className="flex items-center justify-center gap-2 sm:gap-4">
                        <Button
                            variant="secondary"
                            onClick={() => pagination.onPageChange(1)}
                            disabled={pagination.currentPage === 1}
                            className="text-sm"
                        >
                            First
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
                            disabled={pagination.currentPage === 1}
                            className="text-sm"
                        >
                            Previous
                        </Button>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <Button
                            variant="secondary"
                            onClick={() => pagination.onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                            disabled={pagination.currentPage === pagination.totalPages}
                            className="text-sm"
                        >
                            Next
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => pagination.onPageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                            className="text-sm"
                        >
                            Last
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
